import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import type { AssetType } from '@/types/assets';

export async function getRecordCount(selectedAssetType: AssetType, equitySource: 'stock' | 'fund'): Promise<number> {
  try {
    switch (selectedAssetType) {
      case 'equity': {
        if (equitySource === 'stock') {
          const { count, error } = await supabase
            .from('stock_fundamentals')
            .select('*', { count: 'exact', head: true });
          if (error) throw error;
          return count || 0;
        } else {
          const { count, error } = await supabase
            .from('funds_raw')
            .select('*', { count: 'exact', head: true })
            .eq('sa_asset_class', 'equity');
          if (error) throw error;
          return count || 0;
        }
      }

      case 'fixed_income': {
        const { count, error } = await supabase
          .from('funds_raw')
          .select('*', { count: 'exact', head: true })
          .or('sa_asset_class.eq.fixed income,sa_asset_class.eq.fixed_income');
        if (error) throw error;
        return count || 0;
      }

      case 'cash': {
        const { count, error } = await supabase
          .from('funds_raw')
          .select('*', { count: 'exact', head: true })
          .or('sa_asset_class.eq.cash,sa_asset_class.eq.cash_equivalent');
        if (error) throw error;
        return count || 0;
      }

      case 'alternative': {
        const { count: alternativeRawCount, error: altRawError } = await supabase
          .from('funds_raw')
          .select('*', { count: 'exact', head: true })
          .eq('sa_asset_class', 'alternative');
        if (altRawError) throw altRawError;

        const { count: alternativeFundsCount, error: altFundsError } = await supabase
          .from('funds')
          .select('*', { count: 'exact', head: true })
          .eq('asset_class', 'alternative')
          .eq('is_approved', true);
        if (altFundsError) throw altFundsError;

        return (alternativeRawCount || 0) + (alternativeFundsCount || 0);
      }

      case 'multi_asset': {
        const { count: multiAssetRawCount, error: multiRawError } = await supabase
          .from('funds_raw')
          .select('*', { count: 'exact', head: true })
          .eq('sa_asset_class', 'unclassified');
        if (multiRawError) throw multiRawError;

        const { count: multiAssetFundsCount, error: multiFundsError } = await supabase
          .from('funds')
          .select('*', { count: 'exact', head: true })
          .or('asset_class.eq.multi_asset,asset_class.is.null')
          .eq('is_approved', true);
        if (multiFundsError) throw multiFundsError;

        return (multiAssetRawCount || 0) + (multiAssetFundsCount || 0);
      }

      default:
        logger.warn('record-count', 'Unhandled asset type for count', { selectedAssetType });
        return 0;
    }
  } catch (error) {
    logger.error('record-count', 'Error fetching record count', { error });
    return 0;
  }
}
